import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { OctagonAlert, OctagonMinus } from 'lucide-react';
import { WarningsModal } from './WarningsModal';
import { getExpireWarnings } from '@/actions';

const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds

const parseWarningStatus = (data) => {
  if (!data || !data.data) {
    return {
      suspendWarning: false,
      finalWarning: false,
      initialWarning: false,
      warningData: null,
    };
  }

  const { data: responseData } = data;

  const initialWarning =
    responseData.initial_warning_terminals &&
    responseData.initial_warning_message;

  const finalWarning =
    responseData.final_warning_terminals && responseData.final_warning_message;

  const suspendWarning =
    responseData.suspend_warning_terminals &&
    responseData.suspend_warning_message;

  return {
    suspendWarning: !!suspendWarning,
    finalWarning: !!finalWarning,
    initialWarning: !!initialWarning,
    warningData: responseData,
  };
};

export default ({ refetchRef }) => {
  const [warnings, setWarnings] = useState({
    suspend: false,
    final: false,
    initial: false,
  });

  const [warningData, setWarningData] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const intervalRef = useRef(null);
  const userMetas = useSelector((state) => state.reducer.user?.metas);
  const isLoginCredentialsEnabled =
    userMetas?.enable_login_credentials?.value === '1';

  const fetchBannerStatus = useCallback(async () => {
    if (!isLoginCredentialsEnabled) {
      setIsLoading(false);
      setWarnings({ suspend: false, final: false, initial: false });
      setWarningData(null);
      return;
    }

    try {
      const response = await getExpireWarnings();
      const warningStatus = parseWarningStatus(response);

      const activeWarnings = {
        suspend: warningStatus.suspendWarning,
        final: warningStatus.finalWarning,
        initial: warningStatus.initialWarning,
      };

      setWarnings(activeWarnings);
      setWarningData(warningStatus.warningData);

      // Show visible warnings with animation
      Object.keys(activeWarnings).forEach((key) => {
        if (activeWarnings[key]) {
          setTimeout(() => {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }, 10);
        } else {
          setIsVisible((prev) => ({ ...prev, [key]: false }));
        }
      });
    } catch (err) {
      console.error('Error fetching expire warnings:', err);
      setWarnings({ suspend: false, final: false, initial: false });
      setWarningData(null);
    } finally {
      setIsLoading(false);
    }
  }, [isLoginCredentialsEnabled]);

  useEffect(() => {
    fetchBannerStatus();
  }, [fetchBannerStatus]);

  // Set up hourly polling
  useEffect(() => {
    if (!isLoginCredentialsEnabled) return;

    intervalRef.current = setInterval(() => {
      fetchBannerStatus();
    }, ONE_HOUR);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLoginCredentialsEnabled, fetchBannerStatus]);

  // Expose refetch function to parent via ref
  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = fetchBannerStatus;
    }
  }, [refetchRef, fetchBannerStatus]);

  const getWarningMessage = (type) => {
    if (!warningData) return '';

    switch (type) {
      case 'suspend':
        return warningData.suspend_warning_terminals &&
          warningData.suspend_warning_message
          ? `${warningData.suspend_warning_terminals}: ${warningData.suspend_warning_message}`
          : '';
      case 'final':
        return warningData.final_warning_terminals &&
          warningData.final_warning_message
          ? `${warningData.final_warning_terminals}: ${warningData.final_warning_message}`
          : '';
      case 'initial':
        return warningData.initial_warning_terminals &&
          warningData.initial_warning_message
          ? `${warningData.initial_warning_terminals}: ${warningData.initial_warning_message}`
          : '';
      default:
        return '';
    }
  };

  const warningConfigs = [
    {
      type: 'suspend',
      backgroundColor: 'bg-warning-suspend',
      textColor: 'text-white',
      icon: OctagonMinus,
      color: '#FB3748',
    },
    {
      type: 'final',
      backgroundColor: 'bg-warning-final',
      textColor: 'text-primary-text',
      icon: OctagonAlert,
      color: '#ffffff',
    },
    {
      type: 'initial',
      backgroundColor: 'bg-warning-initial',
      textColor: 'text-primary-text',
      icon: OctagonAlert,
      color: '#ffffff',
    },
  ];

  const activeWarnings = warningConfigs.filter(
    (config) => warnings[config.type]
  );
  const shouldShowWarnings = activeWarnings.length >= 1;

  const prepareWarningData = () => {
    if (!warningData || !warningData.warnings) return [];

    const warningsArray = Object.values(warningData.warnings);

    return warningsArray.map((warning) => {
      return {
        ...warning,
      };
    });
  };

  const handleWarningClick = () => {
    setShowModal(true);
  };

  if (isLoading || !shouldShowWarnings) {
    return null;
  }

  return (
    <>
      <div className="pl-3 md:pl-119 w-warning-mobile md:w-warning-desktop ">
        {warningConfigs.map((config) => {
          if (!warnings[config.type]) return null;

          return (
            <div
              key={config.type}
              onClick={handleWarningClick}
              className={`${config.backgroundColor} ${
                config.textColor
              } relative flex text-sm md:h-8 items-center rounded z-99 py-2 md:py-0 px-4 mt-1.5 md:px-6 transition-all duration-300 ease-in-out cursor-pointer hover:opacity-90 ${
                isVisible[config.type]
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}>
              <div className="flex items-center justify-center flex-1 gap-2 ">
                <config.icon size={16} />
                <span
                  className="text-xs leading-[1.5]"
                  style={{ color: config.textColor }}>
                  {getWarningMessage(config.type)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <WarningsModal
        show={showModal}
        setShow={setShowModal}
        data={prepareWarningData()}
        warningsExtraColumns={warningData?.warnings_extra_columns ?? null}
      />
    </>
  );
};
